import React, { useState, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Input, DatePicker, Tooltip, Space, message, Badge, Collapse, Alert, Modal, InputNumber, Popconfirm } from 'antd';
import { ReloadOutlined, SearchOutlined, ClearOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WarningOutlined, InfoCircleOutlined, SyncOutlined, ExclamationCircleOutlined, ThunderboltOutlined, RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useWithdrawalMonitor, useRequeueWithdrawal } from '../hooks/useTransactionManagement';
import { useCoins } from '../hooks/useCoinManagement';
import { useNetworks } from '../hooks/useNetworkManagement';
import { speedUpWithdrawal } from '../api/transaction';
import dayjs from 'dayjs';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const WithdrawalMonitorPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters
  const [searchText, setSearchText] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [coinFilter, setCoinFilter] = useState<string>('all');
  const [networkFilter, setNetworkFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [callbackStatusFilter, setCallbackStatusFilter] = useState<string>('all');
  const [hasErrorsFilter, setHasErrorsFilter] = useState<boolean | undefined>(undefined);
  const [requiresManualFilter, setRequiresManualFilter] = useState<boolean | undefined>(undefined);
  const [dateFromFilter, setDateFromFilter] = useState<dayjs.Dayjs | null>(null);
  const [dateToFilter, setDateToFilter] = useState<dayjs.Dayjs | null>(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // State for sorting
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // State for speed up modal
  const [isSpeedUpModalVisible, setIsSpeedUpModalVisible] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [gasPriceMultiplier, setGasPriceMultiplier] = useState<number>(1.5);
  const [isSpeedingUp, setIsSpeedingUp] = useState<boolean>(false);

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * pageSize;

  // Build filters object
  const buildFilters = () => {
    const filters: any = {};

    if (searchText) {
      filters.search = searchText;
    }

    if (userIdFilter) {
      filters.user_id = parseInt(userIdFilter, 10);
    }

    if (coinFilter && coinFilter !== 'all') {
      console.log('Adding coin_id filter:', coinFilter);
      filters.coin_id = coinFilter;
    }

    if (networkFilter && networkFilter !== 'all') {
      console.log('Adding network_id filter:', networkFilter);
      filters.network_id = networkFilter;
    }

    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }

    if (callbackStatusFilter && callbackStatusFilter !== 'all') {
      filters.callback_status = callbackStatusFilter;
    }

    if (hasErrorsFilter !== undefined) {
      filters.has_errors = hasErrorsFilter;
    }

    if (requiresManualFilter !== undefined) {
      filters.requires_manual_intervention = requiresManualFilter;
    }

    if (dateFromFilter) {
      filters.date_from = dateFromFilter.toISOString();
    }

    if (dateToFilter) {
      filters.date_to = dateToFilter.toISOString();
    }

    return filters;
  };

  // Fetch withdrawal monitor data
  const {
    data: monitorData,
    isLoading,
    error,
    refetch
  } = useWithdrawalMonitor(
    buildFilters(),
    pageSize,
    skip,
    { field: sortField, order: sortOrder }
  );

  // Fetch coins and networks for filters
  const { data: coinsData, isLoading: isCoinsLoading } = useCoins();
  const { data: networksData, isLoading: isNetworksLoading } = useNetworks();

  // Re-queue mutation
  const requeueMutation = useRequeueWithdrawal();

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      console.error('Error fetching withdrawal monitor data:', error);
      message.error('Failed to load withdrawal monitor data. Please try again.');
    }
  }, [error]);

  // Function to handle refreshing data
  const handleRefresh = () => {
    refetch();
  };

  // Handle speed up button click
  const handleSpeedUpClick = (record: any) => {
    setSelectedTransaction(record);
    setGasPriceMultiplier(1.5); // Reset to default
    setIsSpeedUpModalVisible(true);
  };

  // Handle speed up confirmation
  const handleSpeedUpConfirm = async () => {
    if (!selectedTransaction) return;

    try {
      setIsSpeedingUp(true);

      const result = await speedUpWithdrawal(selectedTransaction.id, gasPriceMultiplier);

      if (result.status) {
        message.success(`Transaction sped up successfully! New TXID: ${result.data?.txid || 'N/A'}`);
        setIsSpeedUpModalVisible(false);
        refetch(); // Refresh the list
      } else {
        // Extract error message properly
        let errorMessage = 'Failed to speed up transaction';

        if (result.error) {
          // If error is an object with type, message, details
          if (typeof result.error === 'object') {
            errorMessage = result.error.message || JSON.stringify(result.error);
          } else {
            errorMessage = result.error;
          }
        }

        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Error speeding up transaction:', error);

      // Extract error message properly
      let errorMessage = 'Failed to speed up transaction';

      if (error.response?.data?.error) {
        // If error is an object with type, message, details
        if (typeof error.response.data.error === 'object') {
          errorMessage = error.response.data.error.message || JSON.stringify(error.response.data.error);
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setIsSpeedingUp(false);
    }
  };

  // Handle speed up modal cancel
  const handleSpeedUpCancel = () => {
    setIsSpeedUpModalVisible(false);
    setSelectedTransaction(null);
  };

  // Handle re-queue button click
  const handleRequeueClick = async (record: any) => {
    try {
      await requeueMutation.mutateAsync({
        transactionId: record.id,
        adminNotes: `Re-queued from dashboard by admin at ${new Date().toISOString()}`
      });

      message.success(`Transaction ${record.id.substring(0, 8)}... has been re-queued successfully`);
      refetch(); // Refresh the list
    } catch (error: any) {
      console.error('Error re-queuing transaction:', error);

      // Extract error message properly
      let errorMessage = 'Failed to re-queue transaction';

      if (error.response?.data?.error) {
        // If error is an object with type, message, details
        if (typeof error.response.data.error === 'object') {
          errorMessage = error.response.data.error.message || JSON.stringify(error.response.data.error);
        } else {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    }
  };

  // Function to handle clearing filters
  const handleClearFilters = () => {
    setSearchText('');
    setUserIdFilter('');
    setCoinFilter('all');
    setNetworkFilter('all');
    setStatusFilter('all');
    setCallbackStatusFilter('all');
    setHasErrorsFilter(undefined);
    setRequiresManualFilter(undefined);
    setDateFromFilter(null);
    setDateToFilter(null);
    setCurrentPage(1);
  };

  // Function to handle pagination change
  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  // Format coins data for selection
  const coinOptions = useMemo(() => {
    const coins = Array.isArray(coinsData) ? coinsData : coinsData?.data || [];
    console.log('Withdrawal Monitor - Coin options:', coins.length, 'coins');
    return [
      { value: 'all', label: 'All Coins' },
      ...coins.map((coin: any) => ({
        value: coin._id || coin.id,
        label: `${coin.symbol} - ${coin.full_name || coin.name || ''}`
      }))
    ];
  }, [coinsData]);

  // Format networks data for selection
  const networkOptions = useMemo(() => {
    const networks = Array.isArray(networksData) ? networksData : networksData?.data || [];
    console.log('Withdrawal Monitor - Network options:', networks.length, 'networks');
    return [
      { value: 'all', label: 'All Networks' },
      ...networks.map((network: any) => ({
        value: network._id || network.id,
        label: `${network.title} - ${network.full_name || network.name || ''}`
      }))
    ];
  }, [networksData]);

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'Requested', label: 'Requested' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Broadcasting', label: 'Broadcasting' },
    { value: 'Broadcast', label: 'Broadcast' },
    { value: 'Confirmed', label: 'Confirmed' },
    { value: 'Failed', label: 'Failed' },
    { value: 'Completed', label: 'Completed' }
  ];

  // Callback status options
  const callbackStatusOptions = [
    { value: 'all', label: 'All Callback Status' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Success', label: 'Success' },
    { value: 'Failed', label: 'Failed' }
  ];

  // Render stage status badge
  const renderStageStatus = (stage: any) => {
    if (!stage) return <Tag color="default">Not Started</Tag>;

    const { status, success, error, timestamp } = stage;

    let color = 'default';
    let icon = null;
    let displayText = 'Not Started';

    // Check status first, then success flag
    if (status === 'completed' && success === true) {
      color = 'green';
      icon = <CheckCircleOutlined />;
      displayText = 'Completed';
    } else if (status === 'in_progress') {
      color = 'blue';
      icon = <SyncOutlined spin />;
      displayText = 'In Progress';
    } else if (status === 'failed' || success === false) {
      // Only show Failed if explicitly failed or success is explicitly false
      color = 'red';
      icon = <CloseCircleOutlined />;
      displayText = 'Failed';
    } else if (status === 'not_started' || success === null) {
      // Show Not Started if status is not_started OR success is null (not yet processed)
      color = 'default';
      icon = <ClockCircleOutlined />;
      displayText = 'Not Started';
    }

    const tooltipContent = error || (timestamp ? new Date(timestamp).toLocaleString() : '');

    return (
      <Tooltip title={tooltipContent}>
        <Tag color={color} icon={icon}>
          {displayText}
        </Tag>
      </Tooltip>
    );
  };

  // Render processing stages
  const renderProcessingStages = (record: any) => {
    const { stages } = record;

    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div><Text strong>Requested:</Text> {renderStageStatus(stages?.requested)}</div>
        <div><Text strong>Approval:</Text> {renderStageStatus(stages?.approval)}</div>
        <div><Text strong>Broadcasting:</Text> {renderStageStatus(stages?.broadcasting)}</div>
        <div><Text strong>Broadcast:</Text> {renderStageStatus(stages?.broadcast)}</div>
        <div><Text strong>Confirmation:</Text> {renderStageStatus(stages?.confirmation)}</div>
        <div><Text strong>Callback:</Text> {renderStageStatus(stages?.callback)}</div>
      </Space>
    );
  };

  // Render stage details in expandable row
  const renderStageDetails = (record: any) => {
    const { stages, errors, processing_history, blockchain_data } = record;

    if (!stages) {
      return <Text type="secondary">No detailed stage information available</Text>;
    }

    // Helper to find processing history entries for a specific stage
    const getStageHistory = (stageName: string) => {
      if (!processing_history || !Array.isArray(processing_history)) return [];

      const stageKeywords: Record<string, string[]> = {
        'Requested': ['created', 'requested', 'initiated'],
        'Approval': ['approval', 'approved'],
        'Broadcasting': ['broadcasting', 'queued_for_broadcast'],
        'Broadcast': ['broadcast', 'sent_to_blockchain'],
        'Confirmation': ['confirm', 'confirmed'],
        'Callback': ['callback']
      };

      const keywords = stageKeywords[stageName] || [];
      return processing_history.filter((entry: any) => {
        const action = (entry.action || '').toLowerCase();
        const toState = (entry.to_state || '').toLowerCase();
        return keywords.some(keyword => action.includes(keyword) || toState.includes(keyword));
      });
    };

    const renderDetailedStage = (stageName: string, stage: any) => {
      if (!stage) return null;

      const hasError = stage.status === 'failed' || stage.success === false || stage.error;
      const stageHistory = getStageHistory(stageName);

      return (
        <Card
          size="small"
          title={
            <Space>
              {stageName}
              {hasError && <Tag color="red" icon={<ExclamationCircleOutlined />}>ERROR</Tag>}
            </Space>
          }
          style={{
            marginBottom: 8,
            borderColor: hasError ? '#ff4d4f' : undefined,
            borderWidth: hasError ? 2 : 1
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div><Text strong>Status:</Text> {renderStageStatus(stage)}</div>
            {stage.timestamp && (
              <div><Text strong>Timestamp:</Text> {new Date(stage.timestamp).toLocaleString()}</div>
            )}
            {stage.error && (
              <Alert
                message="Error Details"
                description={stage.error}
                type="error"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
            {stage.txid && (
              <div>
                <Text strong>Transaction ID:</Text>
                <Tooltip title="Click to copy">
                  <Text
                    code
                    copyable
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                  >
                    {stage.txid}
                  </Text>
                </Tooltip>
              </div>
            )}
            {stage.confirmations !== undefined && (
              <div><Text strong>Confirmations:</Text> {stage.confirmations}</div>
            )}
            {stage.attempts !== undefined && stage.attempts > 0 && (
              <div><Text strong>Attempts:</Text> {stage.attempts}</div>
            )}

            {/* Display processing history details for this stage */}
            {stageHistory.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text strong style={{ fontSize: '12px' }}>Processing History:</Text>
                {stageHistory.map((entry: any, idx: number) => {
                  // Filter out redundant or unnecessary fields
                  const hasRelevantDetails = entry.details && Object.keys(entry.details).length > 0;
                  const hasReason = entry.reason;

                  // Skip entries that only have state transitions without meaningful data
                  if (!hasRelevantDetails && !hasReason && !entry.action) {
                    return null;
                  }

                  return (
                    <div key={idx} style={{
                      marginTop: 4,
                      padding: 8,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4,
                      fontSize: '11px'
                    }}>
                      {/* Show timestamp and action in a compact format */}
                      <div style={{ marginBottom: 4 }}>
                        <Text strong>{entry.action}</Text>
                        {entry.timestamp && (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: '10px' }}>
                            {new Date(entry.timestamp).toLocaleString()}
                          </Text>
                        )}
                      </div>

                      {/* Show reason if available */}
                      {hasReason && (
                        <div style={{ marginTop: 2 }}>
                          <Text type="secondary">Reason: </Text>
                          <Text>{entry.reason}</Text>
                        </div>
                      )}

                      {/* Show details in a compact format - only show important fields */}
                      {hasRelevantDetails && (
                        <div style={{ marginTop: 4 }}>
                          <Text strong style={{ fontSize: '10px' }}>Details:</Text>
                          <pre style={{
                            margin: '4px 0 0 0',
                            padding: 4,
                            backgroundColor: '#e6f7ff',
                            borderRadius: 2,
                            fontSize: '10px',
                            maxHeight: '120px',
                            overflow: 'auto'
                          }}>
                            {JSON.stringify(entry.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Space>
        </Card>
      );
    };

    return (
      <div style={{ padding: '16px', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Transaction Details Section - Compact Layout */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Text strong style={{ fontSize: '11px', display: 'block', marginBottom: 4 }}>From Wallet</Text>
              <Tooltip title="Click to copy address">
                <Text
                  copyable
                  code
                  style={{ fontSize: '10px', cursor: 'pointer' }}
                >
                  {record.from_address || 'N/A'}
                </Text>
              </Tooltip>
            </Col>
            <Col span={8}>
              <Text strong style={{ fontSize: '11px', display: 'block', marginBottom: 4 }}>To Address</Text>
              <Tooltip title="Click to copy address">
                <Text
                  copyable
                  code
                  style={{ fontSize: '10px', cursor: 'pointer' }}
                >
                  {record.to_address || 'N/A'}
                </Text>
              </Tooltip>
            </Col>
            {stages.broadcast?.txid && (
              <Col span={8}>
                <Text strong style={{ fontSize: '11px', display: 'block', marginBottom: 4 }}>Transaction ID</Text>
                <Tooltip title="Click to copy transaction ID">
                  <Text
                    copyable
                    code
                    style={{ fontSize: '10px', cursor: 'pointer' }}
                  >
                    {stages.broadcast.txid}
                  </Text>
                </Tooltip>
              </Col>
            )}
          </Row>
        </Card>

        {/* Speed Up Transaction Button - Compact Version */}
        {(record.status === 'Broadcast' || record.status === 'Confirmed' || record.status === 'Broadcasting') && stages.broadcast?.txid && (
          <Alert
            message={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <span>Transaction stuck? Speed it up with higher gas price</span>
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<ThunderboltOutlined />}
                  onClick={() => handleSpeedUpClick(record)}
                >
                  Speed Up
                </Button>
              </Space>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Error Summary Section */}
        {errors && errors.length > 0 && (
          <Alert
            message={`${errors.length} Error${errors.length > 1 ? 's' : ''} Detected`}
            description={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {errors.map((err: any, index: number) => (
                  <div key={index}>
                    <Text strong type="danger">{err.type}:</Text> <Text>{err.message}</Text>
                    {err.timestamp && (
                      <div style={{ color: '#999', fontSize: '11px' }}>
                        {new Date(err.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </Space>
            }
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Callback URL Section - Compact */}
        {record.callback?.url && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: '11px', marginRight: 8 }}>Callback URL:</Text>
            <Tooltip title="Click to copy callback URL">
              <Text
                copyable
                code
                style={{ fontSize: '10px', cursor: 'pointer', wordBreak: 'break-all' }}
              >
                {record.callback.url}
              </Text>
            </Tooltip>
          </div>
        )}

        {/* Stage Details */}
        <Row gutter={16}>
          <Col span={12}>
            {renderDetailedStage('Requested', stages.requested)}
            {renderDetailedStage('Approval', stages.approval)}
            {renderDetailedStage('Broadcasting', stages.broadcasting)}
          </Col>
          <Col span={12}>
            {renderDetailedStage('Broadcast', stages.broadcast)}
            {renderDetailedStage('Confirmation', stages.confirmation)}
            {renderDetailedStage('Callback', stages.callback)}
          </Col>
        </Row>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (text: string) => (
        <Tooltip title="Click to copy ID">
          <span
            style={{ fontFamily: 'monospace', cursor: 'pointer', fontSize: '12px' }}
            onClick={() => {
              navigator.clipboard.writeText(text);
              message.success('ID copied to clipboard');
            }}
          >
            {text.substring(0, 8)}...
            <CopyOutlined style={{ marginLeft: 4, fontSize: '10px', color: '#1890ff' }} />
          </span>
        </Tooltip>
      )
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      sorter: true
    },
    {
      title: 'Coin',
      dataIndex: 'coin',
      key: 'coin',
      width: 80,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Network',
      dataIndex: 'network',
      key: 'network',
      width: 120,
      render: (text: string) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      sorter: true,
      render: (amount: string, record: any) => `${amount} ${record.coin}`
    },
    {
      title: 'Gas Fee',
      dataIndex: 'real_fee',
      key: 'real_fee',
      width: 120,
      render: (realFee: string, record: any) => {
        if (!realFee) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        // Get the network's base coin symbol for the fee
        const feeSymbol = record.network_base_coin || record.coin;

        return (
          <Tooltip title={`Actual gas fee paid: ${realFee} ${feeSymbol}`}>
            <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '12px' }}>
              {parseFloat(realFee).toFixed(6)} <span style={{ color: '#ff9800', fontSize: '10px' }}>{feeSymbol}</span>
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string, record: any) => {
        let color = 'default';
        let icon = null;

        if (status === 'Completed') {
          color = 'green';
          icon = <CheckCircleOutlined />;
        } else if (status === 'Failed') {
          color = 'red';
          icon = <CloseCircleOutlined />;
        } else if (status === 'Broadcast' || status === 'Confirmed') {
          color = 'orange';
          icon = <SyncOutlined spin />;
        } else if (status === 'Requested' || status === 'Approved') {
          color = 'blue';
          icon = <ClockCircleOutlined />;
        } else if (status === 'Broadcasting') {
          color = 'blue';
          icon = <SyncOutlined spin />;
        }

        return (
          <Space direction="vertical" size={2}>
            <Tag color={color} icon={icon}>{status}</Tag>
            {record.has_errors && (
              <Tooltip title="Click to expand and view error details">
                <Tag color="red" icon={<ExclamationCircleOutlined />} style={{ cursor: 'pointer' }}>
                  HAS ERRORS
                </Tag>
              </Tooltip>
            )}
            {record.requires_manual_intervention && (
              <Tag color="orange" icon={<InfoCircleOutlined />}>Manual</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Callback',
      dataIndex: 'callback',
      key: 'callback',
      width: 120,
      render: (callback: any) => {
        if (!callback) return <Tag color="default">Pending</Tag>;

        let color = 'default';
        let icon = null;

        if (callback.status === 'Success' && callback.sent) {
          color = 'green';
          icon = <CheckCircleOutlined />;
        } else if (callback.status === 'Failed') {
          color = 'red';
          icon = <CloseCircleOutlined />;
        } else {
          color = 'orange';
          icon = <ClockCircleOutlined />;
        }

        return (
          <Tooltip title={`Attempts: ${callback.attempts || 0}${callback.timestamp ? ` | ${new Date(callback.timestamp).toLocaleString()}` : ''}`}>
            <Tag color={color} icon={icon}>
              {callback.status}
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Processing Stages',
      key: 'stages',
      width: 200,
      render: (_: any, record: any) => renderProcessingStages(record)
    },
    {
      title: 'Created / Duration',
      key: 'created_duration',
      width: 200,
      sorter: true,
      render: (_: any, record: any) => {
        const createdAt = record.created_at ? new Date(record.created_at) : null;

        // For completed withdrawals, use callback timestamp as end time
        // Otherwise use the latest stage timestamp or updated_at
        let completedAt = null;
        if (record.status === 'Completed') {
          if (record.callback?.timestamp) {
            completedAt = new Date(record.callback.timestamp);
          } else if (record.stages?.confirmation?.timestamp) {
            completedAt = new Date(record.stages.confirmation.timestamp);
          } else if (record.updated_at) {
            completedAt = new Date(record.updated_at);
          }
        }

        // Calculate total duration if both dates exist
        let totalDuration = null;
        if (createdAt && completedAt) {
          const durationMs = completedAt.getTime() - createdAt.getTime();

          // Only show duration if it's positive
          if (durationMs > 0) {
            const seconds = Math.floor(durationMs / 1000);
            const minutes = Math.floor(seconds / 60);
            const hours = Math.floor(minutes / 60);

            if (hours > 0) {
              totalDuration = `${hours}h ${minutes % 60}m`;
            } else if (minutes > 0) {
              totalDuration = `${minutes}m ${seconds % 60}s`;
            } else {
              totalDuration = `${seconds}s`;
            }
          }
        }

        // Calculate callback duration (time from confirmation to callback)
        let callbackDuration = null;
        if (record.callback?.timestamp && record.stages?.confirmation?.timestamp) {
          const callbackTime = new Date(record.callback.timestamp).getTime();
          const confirmTime = new Date(record.stages.confirmation.timestamp).getTime();
          const durationMs = callbackTime - confirmTime;

          // Only show duration if it's positive (callback after confirmation)
          if (durationMs > 0) {
            const seconds = Math.floor(durationMs / 1000);
            const minutes = Math.floor(seconds / 60);

            if (minutes > 0) {
              callbackDuration = `${minutes}m ${seconds % 60}s`;
            } else {
              callbackDuration = `${seconds}s`;
            }
          }
        }

        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: '12px' }}>
              {createdAt ? createdAt.toLocaleString() : '-'}
            </Text>
            {callbackDuration && (
              <Tag color="green" style={{ fontSize: '11px', marginTop: 2 }}>
                📞 {callbackDuration}
              </Tag>
            )}
            {totalDuration && (
              <Tag color="blue" style={{ fontSize: '11px', marginTop: 2 }}>
                ⏱ {totalDuration}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        // Show re-queue button for stuck transactions or transactions with errors
        const isQueued = record.queue_info?.is_queued || false;
        const isNonTerminalStatus = record.status !== 'Completed' && record.status !== 'Failed';

        // Check if transaction is stuck (queued for too long or in non-terminal status without being queued)
        const queuedAt = record.queue_info?.queued_at ? new Date(record.queue_info.queued_at) : null;
        const isQueuedTooLong = queuedAt && (Date.now() - queuedAt.getTime() > 10 * 60 * 1000); // 10 minutes

        // Transaction is stuck if:
        // 1. It's queued and in non-terminal status (might be stuck in queue)
        // 2. It's queued for more than 10 minutes
        // 3. It's in non-terminal status but NOT queued (orphaned transaction)
        const isStuck = (isQueued && isNonTerminalStatus) || isQueuedTooLong || (!isQueued && isNonTerminalStatus);

        const hasErrors = record.has_errors;
        const canRequeue = isStuck || hasErrors || record.requires_manual_intervention;

        return (
          <Space size="small">
            {canRequeue && (
              <Popconfirm
                title="Re-queue Transaction"
                description={`Are you sure you want to re-queue this transaction? This will reset the is_queued flag and allow the discovery service to pick it up again.`}
                onConfirm={() => handleRequeueClick(record)}
                okText="Yes, Re-queue"
                cancelText="Cancel"
                okButtonProps={{ loading: requeueMutation.isPending }}
              >
                <Tooltip title="Re-queue this transaction">
                  <Button
                    type="primary"
                    size="small"
                    icon={<RedoOutlined />}
                    loading={requeueMutation.isPending}
                  >
                    Re-queue
                  </Button>
                </Tooltip>
              </Popconfirm>
            )}
            {record.status === 'Broadcast' && record.txid && (
              <Tooltip title="Speed up transaction with higher gas price">
                <Button
                  type="default"
                  size="small"
                  icon={<ThunderboltOutlined />}
                  onClick={() => handleSpeedUpClick(record)}
                >
                  Speed Up
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col span={12}>
          <Title level={2}>Withdrawal Monitor</Title>
          <Text type="secondary">
            Monitor withdrawal requests and their processing stages
          </Text>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        {/* Search and Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <div className="filter-label">Search</div>
            <Input
              placeholder="Search by ID or txid"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              onPressEnter={() => setCurrentPage(1)}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">User ID</div>
            <Input
              placeholder="User ID"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              allowClear
              type="number"
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Coin</div>
            <SearchableSelect
              placeholder="Select Coin"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={coinFilter}
              onChange={(value) => {
                setCoinFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isCoinsLoading}
              options={coinOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Network</div>
            <SearchableSelect
              placeholder="Select Network"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={networkFilter}
              onChange={(value) => {
                setNetworkFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isNetworksLoading}
              options={networkOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Status</div>
            <SearchableSelect
              placeholder="Select Status"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={statusOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Callback</div>
            <SearchableSelect
              placeholder="Callback Status"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={callbackStatusFilter}
              onChange={(value) => {
                setCallbackStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={callbackStatusOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Has Errors</div>
            <SearchableSelect
              placeholder="Has Errors"
              style={{ width: '100%' }}
              value={hasErrorsFilter === undefined ? 'all' : hasErrorsFilter ? 'yes' : 'no'}
              onChange={(value) => {
                setHasErrorsFilter(value === 'all' ? undefined : value === 'yes');
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={3}>
            <div className="filter-label">Manual Intervention</div>
            <SearchableSelect
              placeholder="Manual"
              style={{ width: '100%' }}
              value={requiresManualFilter === undefined ? 'all' : requiresManualFilter ? 'yes' : 'no'}
              onChange={(value) => {
                setRequiresManualFilter(value === 'all' ? undefined : value === 'yes');
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]}
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">Date From</div>
            <DatePicker
              placeholder="Select Date From"
              style={{ width: '100%' }}
              value={dateFromFilter}
              onChange={(date) => {
                setDateFromFilter(date);
                setCurrentPage(1);
              }}
              allowClear
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">Date To</div>
            <DatePicker
              placeholder="Select Date To"
              style={{ width: '100%' }}
              value={dateToFilter}
              onChange={(date) => {
                setDateToFilter(date);
                setCurrentPage(1);
              }}
              allowClear
            />
          </Col>
          <Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              style={{ width: '100%' }}
            >
              Clear
            </Button>
          </Col>
        </Row>

        {/* Summary Stats */}
        {monitorData?.success && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <Text type="secondary">Total Withdrawals</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {monitorData.pagination?.total || 0}
                </Title>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Text type="secondary">Current Page</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {monitorData.data?.length || 0}
                </Title>
              </Card>
            </Col>
          </Row>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={monitorData?.data || []}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: monitorData?.pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, total)} of ${total} withdrawals`
          }}
          onChange={handleTableChange}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1800 }}
          size="small"
          expandable={{
            expandedRowRender: renderStageDetails,
            rowExpandable: (record) => !!record.stages
          }}
        />
      </Card>

      {/* Speed Up Transaction Modal */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#ff9800' }} />
            <span>Speed Up Transaction</span>
          </Space>
        }
        open={isSpeedUpModalVisible}
        onOk={handleSpeedUpConfirm}
        onCancel={handleSpeedUpCancel}
        confirmLoading={isSpeedingUp}
        okText="Speed Up"
        okButtonProps={{ danger: true }}
        width={600}
      >
        {selectedTransaction && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
              message="Warning: Transaction Replacement"
              description="This will create a new transaction with the same nonce but higher gas price. The old transaction will be replaced and will not be mined."
              type="warning"
              showIcon
            />

            <div>
              <Text strong>Transaction ID:</Text>
              <br />
              <Text code copyable style={{ fontSize: '12px' }}>
                {selectedTransaction.stages?.broadcast?.txid || selectedTransaction.id}
              </Text>
            </div>

            <div>
              <Text strong>Amount:</Text> {selectedTransaction.amount} {selectedTransaction.coin}
            </div>

            <div>
              <Text strong>Network:</Text> {selectedTransaction.network}
            </div>

            <div>
              <Text strong>Current Status:</Text> <Tag color="blue">{selectedTransaction.status}</Tag>
            </div>

            <div>
              <Text strong>Gas Price Multiplier:</Text>
              <br />
              <InputNumber
                min={1.1}
                max={100.0}
                step={0.1}
                value={gasPriceMultiplier}
                onChange={(value) => setGasPriceMultiplier(value || 1.5)}
                style={{ width: '100%', marginTop: 8 }}
                addonAfter="x"
              />
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: '11px', width: '100%', marginBottom: 4 }}>
                  Quick select:
                </Text>
                <Button size="small" onClick={() => setGasPriceMultiplier(1.5)}>1.5x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(2.0)}>2x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(5.0)}>5x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(10.0)}>10x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(20.0)}>20x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(50.0)}>50x</Button>
                <Button size="small" onClick={() => setGasPriceMultiplier(100.0)}>100x</Button>
              </div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 8 }}>
                Recommended: 1.5x-2x for normal, 5x-10x for urgent, 20x+ for critical stuck transactions
              </Text>
            </div>

            <Alert
              message="Important Notes"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>The new transaction will have {gasPriceMultiplier}x higher gas price</li>
                  <li>This will help the transaction get mined faster</li>
                  <li>The old transaction will be replaced and discarded</li>
                  <li>You will be charged the higher gas fee</li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default WithdrawalMonitorPage;


